import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaFileAlt,
  FaClipboardList,
  FaUser,
  FaFileMedical,
  FaFileInvoice,
} from "react-icons/fa";

const WorkflowList = () => {
  // Dummy data for workflows (replace with API data later)
  const [workflows, setWorkflows] = useState([
    {
      id: 1,
      name: "IPD Discharge Approval",
      steps: [
        { id: 1, name: "Step 1", conditions: [], approvers: [] },
        { id: 2, name: "Step 2", conditions: [], approvers: [] },
      ],
    },
    {
      id: 2,
      name: "Pharmacy Order Approval",
      steps: [{ id: 1, name: "Step 1", conditions: [], approvers: [] }],
    },
  ]);

  const navigate = useNavigate();
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef(null);

  // Workflow categories and types
  const workflowOptions = [
    {
      category: "Request",
      types: [
        {
          name: "Purchase Request",
          query: "purchase-request",
          icon: <FaFileAlt className="text-blue-500" />,
        },
        {
          name: "Medicine Request",
          query: "medicine-request",
          icon: <FaClipboardList className="text-green-500" />,
        },
        {
          name: "Pharmacy Request",
          query: "pharmacy-request",
          icon: <FaFileMedical className="text-purple-500" />,
        },
        {
          name: "Request by Patient",
          query: "request-by-patient",
          icon: <FaUser className="text-orange-500" />,
        },
        {
          name: "Service Request",
          query: "service-request",
          icon: <FaFileInvoice className="text-pink-500" />,
        },
      ],
    },
    {
      category: "Order",
      types: [
        {
          name: "Purchase Order",
          query: "purchase-order",
          icon: <FaFileAlt className="text-blue-500" />,
        },
      ],
    },
    {
      category: "IPD",
      types: [
        {
          name: "Discharge Summary",
          query: "discharge",
          icon: <FaFileMedical className="text-purple-500" />,
        },
      ],
    },
  ];

  // Close the menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Delete a workflow
  const deleteWorkflow = (workflowId) => {
    const confirmDelete = window.confirm(
      `Are you sure you want to delete this workflow?`
    );
    if (confirmDelete) {
      const updatedWorkflows = workflows.filter(
        (workflow) => workflow.id !== workflowId
      );
      setWorkflows(updatedWorkflows);
    }
  };

  // Edit a workflow (navigate to WorkflowSetup with pre-filled data)
  const editWorkflow = (workflowId) => {
    navigate("/setup", { state: { workflowId } });
  };

  // Handle workflow type selection
  const handleWorkflowTypeSelection = (query) => {
    setShowMenu(false);
    navigate(`/new/setup/workflow/new?type=${query}`);
  };

  return (
    <div className="p-6 space-y-6 bg-gray-100 min-h-screen">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-800">
          Workflow Management
        </h1>
        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="bg-green-500 text-white px-6 py-2 rounded hover:bg-green-600 shadow-md"
          >
            Create
          </button>
          {showMenu && (
            <div className="absolute right-0 mt-2 w-80 bg-white border rounded shadow-lg z-50">
              <ul className="divide-y divide-gray-200">
                {workflowOptions.map((category) => (
                  <li key={category.category} className="p-4">
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">
                      {category.category}
                    </h3>
                    <ul className="space-y-2">
                      {category.types.map((type) => (
                        <li key={type.query}>
                          <button
                            onClick={() =>
                              handleWorkflowTypeSelection(type.query)
                            }
                            className="w-full flex items-center text-left px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-md text-gray-700"
                          >
                            <span className="mr-2">{type.icon}</span>
                            {type.name}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      <div className="space-y-4">
        {workflows.map((workflow) => (
          <div
            key={workflow.id}
            className="p-4 border rounded-lg bg-white shadow-sm hover:shadow-md transition-shadow flex justify-between items-center"
          >
            <div>
              <h2 className="text-lg font-semibold text-gray-700">
                {workflow.name}
              </h2>
              <p className="text-sm text-gray-500">
                {workflow.steps.length} Step(s)
              </p>
            </div>
            <div className="flex space-x-4">
              <button
                onClick={() => editWorkflow(workflow.id)}
                className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition-colors"
              >
                Edit
              </button>
              <button
                onClick={() => deleteWorkflow(workflow.id)}
                className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        ))}

        {workflows.length === 0 && (
          <div className="text-center py-6 bg-white rounded-lg shadow-sm">
            <p className="text-gray-500">No workflows found.</p>
            <button
              onClick={() => setShowMenu(true)}
              className="mt-4 bg-green-500 text-white px-6 py-2 rounded hover:bg-green-600 shadow-md"
            >
              Create Your First Workflow
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkflowList;
